// src/pages/DashboardGroupsTestPage.tsx
import React, { useState, useEffect } from 'react';
import DashboardGroups from '@/components/DashboardGroups/DashboardGroups';
import { authStorage } from '../lib/storage';

const DashboardGroupsTestPage: React.FC = () =>
{
  // Configuration state
  const [dashboardId, setDashboardId] = useState<number>(205611);
  const [inputDashboardId, setInputDashboardId] = useState<string>('205611');
  const [projectId, setProjectId] = useState<string>('');
  const [inputProjectId, setInputProjectId] = useState<string>('');
  const [searchText, setSearchText] = useState<string>('');
  const [inputSearchText, setInputSearchText] = useState<string>('');
  const [pageSize, setPageSize] = useState<number>(130);
  const [cardViewMode, setCardViewMode] = useState<'compact' | 'detailed'>('detailed');
  const [columnWidth, setColumnWidth] = useState<number>(350);
  const [resizableColumns, setResizableColumns] = useState<boolean>(false);
  const [hasToken, setHasToken] = useState<boolean>(false);
  const [isSimulatingLoading, setIsSimulatingLoading] = useState<boolean>(false);
  const [key, setKey] = useState<number>(0); // For forcing re-render

  // Check for token on mount
  useEffect(() =>
  {
    const token = authStorage.getAccessToken();
    setHasToken(!!token);
  }, []);

  // Handle form submission
  const handleApplySettings = (e: React.FormEvent) =>
  {
    e.preventDefault();
    const newDashboardId = parseInt(inputDashboardId, 10);
    if (!isNaN(newDashboardId) && newDashboardId > 0)
    {
      setDashboardId(newDashboardId);
    }
    setProjectId(inputProjectId);
    setSearchText(inputSearchText);
    setKey(prev => prev + 1); // Force re-render
  };

  // Callback handlers
  const handleDataLoaded = (_data: any) => {};

  const handleError = (error: Error) =>
  {
    console.error('❌ onError:', error);
  };

  const handleCardMoved = (_cardId: number, _fromColumnId: number, _toColumnId: number) => {};

  const handleOpenInfoWindow = (_conversation: any) => {};

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      {/* Header */}
      <div
        style={{
          padding: '20px',
          backgroundColor: 'white',
          borderBottom: '1px solid #ddd',
          marginBottom: '20px',
        }}
      >
        <h1 style={{ margin: '0 0 15px 0', color: '#333' }}>DashboardGroups Test Page</h1>

        {/* Token Status */}
        <div
          style={{
            padding: '10px 15px',
            marginBottom: '15px',
            borderRadius: '4px',
            backgroundColor: hasToken ? '#e8f5e9' : '#ffebee',
            color: hasToken ? '#2e7d32' : '#c62828',
          }}
        >
          {hasToken
            ? '✅ Token detected from session - using dynamic authentication'
            : '⚠️ No token found - please login first or check authentication'}
        </div>

        {/* Configuration Form */}
        <form onSubmit={handleApplySettings} style={{ marginBottom: '15px' }}>
          <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', marginBottom: '15px' }}>
            {/* Dashboard ID */}
            <div>
              <label
                style={{
                  display: 'block',
                  marginBottom: '5px',
                  fontWeight: 'bold',
                  fontSize: '14px',
                }}
              >
                Dashboard ID:
              </label>
              <input
                type="number"
                value={inputDashboardId}
                onChange={e => setInputDashboardId(e.target.value)}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  width: '100px',
                }}
              />
            </div>

            {/* Project ID */}
            <div>
              <label
                style={{
                  display: 'block',
                  marginBottom: '5px',
                  fontWeight: 'bold',
                  fontSize: '14px',
                }}
              >
                Project ID (optional):
              </label>
              <input
                type="text"
                value={inputProjectId}
                onChange={e => setInputProjectId(e.target.value)}
                placeholder="e.g., 123"
                style={{
                  padding: '8px 12px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  width: '150px',
                }}
              />
            </div>

            {/* Search Text */}
            <div>
              <label
                style={{
                  display: 'block',
                  marginBottom: '5px',
                  fontWeight: 'bold',
                  fontSize: '14px',
                }}
              >
                Search Text:
              </label>
              <input
                type="text"
                value={inputSearchText}
                onChange={e => setInputSearchText(e.target.value)}
                placeholder="Search..."
                style={{
                  padding: '8px 12px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  width: '200px',
                }}
              />
            </div>

            {/* Page Size */}
            <div>
              <label
                style={{
                  display: 'block',
                  marginBottom: '5px',
                  fontWeight: 'bold',
                  fontSize: '14px',
                }}
              >
                Page Size:
              </label>
              <input
                type="number"
                value={pageSize}
                onChange={e => setPageSize(parseInt(e.target.value, 10) || 130)}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  width: '100px',
                }}
              />
            </div>

            {/* Column Width */}
            <div>
              <label
                style={{
                  display: 'block',
                  marginBottom: '5px',
                  fontWeight: 'bold',
                  fontSize: '14px',
                }}
              >
                Column Width:
              </label>
              <input
                type="number"
                value={columnWidth}
                onChange={e => setColumnWidth(parseInt(e.target.value, 10) || 350)}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  width: '100px',
                }}
              />
            </div>
          </div>

          {/* View Options */}
          <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', marginBottom: '15px' }}>
            {/* Card View Mode */}
            <div>
              <label
                style={{
                  display: 'block',
                  marginBottom: '5px',
                  fontWeight: 'bold',
                  fontSize: '14px',
                }}
              >
                Card View Mode:
              </label>
              <select
                value={cardViewMode}
                onChange={e => setCardViewMode(e.target.value as 'compact' | 'detailed')}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  width: '150px',
                }}
              >
                <option value="compact">Compact</option>
                <option value="detailed">Detailed</option>
              </select>
            </div>

            {/* Resizable Columns */}
            <label
              style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingTop: '25px' }}
            >
              <input
                type="checkbox"
                checked={resizableColumns}
                onChange={e => setResizableColumns(e.target.checked)}
              />
              <span>Resizable Columns</span>
            </label>

            {/* Simulate Loading */}
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                paddingTop: '25px',
                color: '#1877f2',
                fontWeight: 500,
              }}
            >
              <input
                type="checkbox"
                checked={isSimulatingLoading}
                onChange={e => setIsSimulatingLoading(e.target.checked)}
              />
              <span>Simulate Loading</span>
            </label>
          </div>

          <button
            type="submit"
            style={{
              padding: '10px 20px',
              backgroundColor: '#1877f2',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 'bold',
            }}
          >
            Apply Settings & Reload
          </button>
        </form>

        {/* Current Config Info */}
        <div
          style={{
            fontSize: '12px',
            color: '#666',
            backgroundColor: '#f8f9fa',
            padding: '10px',
            borderRadius: '4px',
          }}
        >
          <strong>Current Config:</strong> Dashboard #{dashboardId}
          {projectId && ` | Project: ${projectId}`}
          {searchText && ` | Search: "${searchText}"`}
          {` | Page Size: ${pageSize} | Column Width: ${columnWidth}px | Mode: ${cardViewMode}`}
        </div>
      </div>

      {/* DashboardGroups Component */}
      <div style={{ padding: '0 20px' }}>
        {isSimulatingLoading ? (
          <div className="dashboard-groups flex gap-6 overflow-x-auto pb-4 p-4">
            {[1, 2, 3].map(i => (
              <div
                key={i}
                className="flex-shrink-0 bg-gray-100 rounded-lg"
                style={{ width: `${columnWidth}px` }}
              >
                {/* Header skeleton */}
                <div className="p-3 border-gray-300 flex items-center justify-between">
                  <div className="animate-pulse h-5 bg-gray-200 rounded w-24"></div>
                </div>
                {/* Cards skeleton */}
                <div className="p-3 border-t border-gray-300 space-y-3">
                  {[1, 2, 3].map(j => (
                    <div key={j} className="animate-pulse bg-white rounded shadow-sm p-4">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                        <div className="flex-1">
                          <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                          <div className="h-3 bg-gray-100 rounded w-1/2"></div>
                        </div>
                      </div>
                      <div className="space-y-2 mb-3">
                        <div className="h-3 bg-gray-200 rounded w-full"></div>
                        <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                      </div>
                      <div className="flex gap-2">
                        <div className="h-5 bg-gray-200 rounded w-16"></div>
                        <div className="h-5 bg-gray-200 rounded w-12"></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <DashboardGroups
            key={key}
            dashboardId={dashboardId}
            projectId={projectId}
            searchText={searchText}
            pageSize={pageSize}
            cardViewMode={cardViewMode}
            columnWidth={columnWidth}
            resizableColumns={resizableColumns}
            onDataLoaded={handleDataLoaded}
            onError={handleError}
            onCardMoved={handleCardMoved}
            onOpenInfoWindow={handleOpenInfoWindow}
          />
        )}
      </div>

      {/* Debug Info */}
      <div style={{ padding: '20px' }}>
        <details style={{ marginTop: '20px' }}>
          <summary style={{ cursor: 'pointer', fontWeight: 'bold', marginBottom: '10px' }}>
            Debug: Component Props
          </summary>
          <pre
            style={{
              backgroundColor: '#f8f9fa',
              padding: '15px',
              borderRadius: '4px',
              overflow: 'auto',
              maxHeight: '400px',
              fontSize: '12px',
            }}
          >
            {JSON.stringify(
              {
                dashboardId,
                projectId,
                searchText,
                pageSize,
                cardViewMode,
                columnWidth,
                resizableColumns,
              },
              null,
              2,
            )}
          </pre>
        </details>
      </div>
    </div>
  );
};

export default DashboardGroupsTestPage;
